import {
    Column,
    Container,
    colorWithOpacity,
    cssVar,
    Grid,
    getFormattedCurrency,
    Header,
    HomeIcon,
    Label,
    Row,
    Separator,
    Tabbed,
    ToolTip,
    useAlignments,
    useScreen,
    useSizes,
    VehicleIcon,
} from 'metrix-common3';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ProfileMessages from '../Messages';

const HeaderCell = styled(Container)`
    background: ${cssVar.colorStrokeVeryLight};
`;

const BodyCell = styled(Container)`
    ${({ firstRow }) => firstRow && `border-top: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-bottom: 1px solid ${cssVar.colorStrokeVeryLight};
    ${({ firstCell }) => firstCell && `border-left: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-right: 1px solid ${cssVar.colorStrokeVeryLight};
`;

const FooterCell = styled(Container)`
    background: ${cssVar.colorStrokeVeryLight};
    ${({ hasTopBorder }) => hasTopBorder && `border-top: 2px solid ${cssVar.colorStrokeDark};`}
`;

const SeparatorCell = styled(Container)`
    border-bottom: 2px solid ${cssVar.colorStrokeDark};
    ${({ firstCell }) => firstCell && `border-left: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-right: 1px solid ${cssVar.colorStrokeVeryLight};
    padding: 0;
`;

const MobileCell = styled(Container)`
    ${({ title }) => title && `background: ${cssVar.colorStrokeVeryLight};`}
    border: 1px solid ${colorWithOpacity(cssVar.colorStrokeDark, cssVar.opacityLighter)};
    flex: 100%;
`;

const MobileCellInner = styled(Container)`
    flex: 100%;
`;

const ToolTipContainer = styled(Container)`
    padding-top: 4px
`;

const ProfileRisks = (props) => {
    const sizes = useSizes();
    const alignments = useAlignments();
    const screen = useScreen();

    const [currentRisk, setCurrentRisk] = useState(0);

    const getTable = (coverages, showPrice, totals) => {
        return (
            <Grid>
                {!screen?.isMobile && (
                    <Row nogutter>
                        <Column nogutter xs={showPrice ? 3 : 4} md={6}>
                            <HeaderCell fullSize />
                        </Column>
                        <Column nogutter xs={showPrice ? 3 : 4} md={showPrice ? 2 : 3}>
                            <HeaderCell fullSize>
                                <Label
                                    bold
                                    left={sizes.xs}
                                    top={sizes.xs}
                                    bottom={sizes.xs}
                                    right={sizes.xs}
                                    textAlign={alignments.right}
                                    text={<FormattedMessage id='Profile_CommonComponents_Risks.limit' />}
                                />
                            </HeaderCell>
                        </Column>
                        <Column nogutter xs={showPrice ? 3 : 4} md={showPrice ? 2 : 3}>
                            <HeaderCell fullSize>
                                <Label
                                    bold
                                    left={sizes.xs}
                                    top={sizes.xs}
                                    bottom={sizes.xs}
                                    right={sizes.xs}
                                    textAlign={alignments.right}
                                    text={<FormattedMessage id='Profile_CommonComponents_Risks.deductible' />}
                                />
                            </HeaderCell>
                        </Column>
                        {showPrice && (
                            <Column nogutter xs={3} md={2}>
                                <HeaderCell fullSize>
                                    <Label
                                        bold
                                        left={sizes.xs}
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.amount' />}
                                    />
                                </HeaderCell>
                            </Column>
                        )}
                    </Row>
                )}
                {coverages.map((coverage, index) => {
                    const limitAmount = coverage.formattedLimit;
                    const deductibleAmount = coverage.formattedDeductible;
                    let premiumAmount = coverage.formattedPremium;
                    const available = coverage.status !== 'rejected';
                    const included =
                        coverage.type === 'Mandatory' ||
                        (coverage.status !== 'declined' &&
                            (coverage.status === 'accepted' ||
                                (coverage.status === 'proposed' &&
                                    (!coverage.grossPremium || coverage.grossPremium.value === 0))));

                    const labelSuffix = !available
                        ? '[i][pc] - not available[pc][i]'
                        : !included
                          ? '[i][pc] - not included[pc][i]'
                          : '';

                    if (available && !included && Number.isNaN(coverage.premium?.value)) {
                        premiumAmount = '';
                    }

                    const label = coverage.label + labelSuffix;

                    const messages =
                        props.context.session.userType === 'broker' &&
                        coverage.messages &&
                        coverage.messages.length > 0 &&
                        coverage.messages;

                    return screen?.isMobile ? (
                        // biome-ignore lint/suspicious/noArrayIndexKey: Coverage items don't have unique IDs
                        <Row nogutter key={`mobile-coverage-${index}`}>
                            <Column xs={12} nogutter>
                                <MobileCell title verticalAlignment={alignments.center} inline>
                                    <Label
                                        bold
                                        left={sizes.xs}
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.left}
                                        text={label}
                                    />
                                    {messages && (
                                        <ToolTipContainer>
                                            <ToolTip>
                                                <ProfileMessages
                                                    messages={messages}
                                                    size={sizes.sm}
                                                    title={coverage.label}
                                                    titleSize={sizes.xs}
                                                />
                                            </ToolTip>
                                        </ToolTipContainer>
                                    )}
                                </MobileCell>
                            </Column>
                            <Column xs={showPrice ? 4 : 6} nogutter>
                                <MobileCell fullSize>
                                    <MobileCellInner fullSize left={sizes.xs} top={sizes.xs} bottom={sizes.xs}>
                                        <Label
                                            bold
                                            textAlign={alignments.right}
                                            text={<FormattedMessage id='Profile_CommonComponents_Risks.limit' />}
                                        />
                                        <Label textAlign={alignments.right} text={limitAmount} />
                                    </MobileCellInner>
                                </MobileCell>
                            </Column>
                            <Column xs={showPrice ? 4 : 6} nogutter>
                                <MobileCell fullSize>
                                    <MobileCellInner fullSize left={sizes.xs} top={sizes.xs} bottom={sizes.xs}>
                                        <Label
                                            bold
                                            textAlign={alignments.right}
                                            text={<FormattedMessage id='Profile_CommonComponents_Risks.deductible' />}
                                        />
                                        <Label textAlign={alignments.right} text={deductibleAmount} />
                                    </MobileCellInner>
                                </MobileCell>
                            </Column>
                            {showPrice && (
                                <Column xs={showPrice ? 4 : 6} nogutter>
                                    <MobileCell fullSize>
                                        <MobileCellInner fullSize left={sizes.xs} top={sizes.xs} bottom={sizes.xs}>
                                            <Label
                                                bold
                                                textAlign={alignments.right}
                                                text={<FormattedMessage id='Profile_CommonComponents_Risks.price' />}
                                            />
                                            <Label textAlign={alignments.right} text={premiumAmount} />
                                        </MobileCellInner>
                                    </MobileCell>
                                </Column>
                            )}
                        </Row>
                    ) : (
                        // biome-ignore lint/suspicious/noArrayIndexKey: Coverage items don't have unique IDs
                        <Row nogutter key={`coverage-${index}`}>
                            <Column nogutter xs={showPrice ? 3 : 4} md={6}>
                                <BodyCell
                                    firstCell
                                    firstRow={index === 0}
                                    fullSize
                                    inline
                                    verticalAlignment={alignments.center}
                                >
                                    <Label
                                        left={sizes.xs}
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.left}
                                        text={label}
                                    />
                                    {messages && (
                                        <ToolTipContainer>
                                            <ToolTip>
                                                <ProfileMessages
                                                    messages={messages}
                                                    size={sizes.sm}
                                                    title={coverage.label}
                                                    titleSize={sizes.xs}
                                                />
                                            </ToolTip>
                                        </ToolTipContainer>
                                    )}
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={showPrice ? 3 : 4} md={showPrice ? 2 : 3}>
                                <BodyCell lastCell={showPrice === false} firstRow={index === 0} fullSize>
                                    <Label
                                        left={sizes.xs}
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={limitAmount}
                                    />
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={showPrice ? 3 : 4} md={showPrice ? 2 : 3}>
                                <BodyCell firstRow={index === 0} fullSize>
                                    <Label
                                        left={sizes.xs}
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={deductibleAmount}
                                    />
                                </BodyCell>
                            </Column>
                            {showPrice && (
                                <Column nogutter xs={3} md={2}>
                                    <BodyCell lastCell firstRow={index === 0} fullSize>
                                        <Label
                                            right={sizes.xs}
                                            top={sizes.xs}
                                            bottom={sizes.xs}
                                            textAlign={alignments.right}
                                            text={premiumAmount}
                                        />
                                    </BodyCell>
                                </Column>
                            )}
                        </Row>
                    );
                })}
                {totals && (
                    <React.Fragment>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize hasTopBorder>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.premium_total' />}
                                    />
                                </FooterCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize hasTopBorder>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.premiumTotal}
                                    />
                                </FooterCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <BodyCell firstCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.surcharges' />}
                                    />
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <BodyCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.surcharges}
                                    />
                                </BodyCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <BodyCell firstCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.discounts' />}
                                    />
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <BodyCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.discount}
                                    />
                                </BodyCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <BodyCell firstCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.fees' />}
                                    />
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <BodyCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.fees}
                                    />
                                </BodyCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize hasTopBorder>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.subtotal' />}
                                    />
                                </FooterCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize hasTopBorder>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.subTotal}
                                    />
                                </FooterCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <BodyCell firstCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.taxes' />}
                                    />
                                </BodyCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <BodyCell fullSize>
                                    <Label
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.taxes}
                                    />
                                </BodyCell>
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <SeparatorCell firstCell fullSize />
                            </Column>
                            <Column nogutter xs={6}>
                                <SeparatorCell fullSize />
                            </Column>
                        </Row>
                        <Row nogutter>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_CommonComponents_Risks.grand_total' />}
                                    />
                                </FooterCell>
                            </Column>
                            <Column nogutter xs={6}>
                                <FooterCell fullSize>
                                    <Label
                                        bold
                                        top={sizes.xs}
                                        bottom={sizes.xs}
                                        right={sizes.xs}
                                        textAlign={alignments.right}
                                        text={totals.grandTotal}
                                    />
                                </FooterCell>
                            </Column>
                        </Row>
                    </React.Fragment>
                )}
            </Grid>
        );
    };

    const getRisk = (risk) => {
        const coverages = risk.coverages;

        if (coverages.some((x) => x.coverageCode.startsWith('ERQK') && x.status !== 'accepted')) {
            coverages
                .filter((x) => x.coverageCode.startsWith('ERQK'))
                .forEach((x) => {
                    x.status = 'declined';
                });
        }

        const mandatoryCoverages = coverages.filter((x) => x.type === 'Mandatory');
        const optionalCoverages = coverages.filter((x) => x.type === 'Optional');
        const showPrice = props.policySummary.status !== 'lost';
        const showOptionalCoverages = optionalCoverages && optionalCoverages.length > 0;

        // TODO: Currency should be dynamic
        const currency = 'CAD';
        const culture = props.context.session.culture;

        // Use currentQuote if provided (when switching tabs in ProfileQuotes), otherwise use policySummary
        const dataSource = props.currentQuote || props.policySummary;

        const annualPremiumAmount = dataSource.estimatedAnnualPremium?.value || 0;
        const taxesAmount = dataSource.estimatedTaxes?.value || 0;
        const feesAmount = dataSource.estimatedFees?.value || 0;
        const surchargesAmount = dataSource.estimatedSurcharges?.value || 0;
        const discountsAmount = dataSource.estimatedDiscounts?.value || 0;

        const premiumTotalAmount = annualPremiumAmount;
        const formattedPremiumTotalAmount = getFormattedCurrency(premiumTotalAmount, currency, culture);
        const formattedSurchargesAmount = dataSource.formattedEstimatedSurcharges || '$0.00';
        const formattedDiscountsAmount = dataSource.formattedEstimatedDiscounts || '$0.00';
        const formattedFeesAmount = dataSource.formattedEstimatedFees || '$0.00';
        const formattedTaxesAmount = dataSource.formattedEstimatedTaxes || '$0.00';

        const subTotalAmount = premiumTotalAmount + surchargesAmount + discountsAmount + feesAmount;
        const formattedSubTotalAmount = getFormattedCurrency(subTotalAmount, currency, culture);

        const grandTotalAmount = subTotalAmount + taxesAmount;
        const formattedGrandTotalAmount = getFormattedCurrency(grandTotalAmount, currency, culture);

        const totals = {
            premiumTotal: formattedPremiumTotalAmount,
            surcharges: formattedSurchargesAmount,
            discount: formattedDiscountsAmount,
            fees: formattedFeesAmount,
            subTotal: formattedSubTotalAmount,
            taxes: formattedTaxesAmount,
            grandTotal: formattedGrandTotalAmount,
        };

        const icon =
            props.policySummary.lineOfBusiness === 'auto' ? (
                <VehicleIcon primary={cssVar.colorSecondary} />
            ) : (
                <HomeIcon primary={cssVar.colorTertiaryText} />
            );

        return (
            <Container bottom={sizes.sm}>
                <Container inline>
                    {icon}
                    <Container verticalAlignment={alignments.bottom}>
                        <Header
                            size={sizes.md}
                            left={sizes.sm}
                            textAlign={alignments.left}
                            text={risk.fullDescription}
                        />
                    </Container>
                </Container>
                <Container top={sizes.sm} bottom={sizes.sm}>
                    <Header
                        size={sizes.sm}
                        top={sizes.xs}
                        bottom={sizes.xs}
                        text={<FormattedMessage id='Profile_CommonComponents_Risks.mainCoverage' />}
                        textAlign={alignments.left}
                    />
                    {getTable(mandatoryCoverages, showPrice, showOptionalCoverages ? null : totals)}
                </Container>
                {showOptionalCoverages && (
                    <Container bottom={sizes.sm}>
                        <Header
                            size={sizes.sm}
                            top={sizes.xs}
                            bottom={sizes.xs}
                            text={<FormattedMessage id='Profile_CommonComponents_Risks.extraProtection' />}
                            textAlign={alignments.left}
                        />
                        {getTable(optionalCoverages, showPrice, totals)}
                    </Container>
                )}
                {(props.policySummary.lineOfBusiness === 'auto' || props.policySummary.lineOfBusiness === 'home') && (
                    <React.Fragment>
                        <Separator orientation={'horizontal'} top={sizes.xs} bottom={sizes.xs} solid />
                        <Container top={sizes.sm} bottom={sizes.sm}>
                            <Header
                                size={sizes.sm}
                                text={<FormattedMessage id='Profile_CommonComponents_Risks.informationRecap' />}
                                textAlign={alignments.left}
                            />
                            <Grid>
                                <Row>
                                    {risk.recaps?.map((recap, recapIndex) => {
                                        return (
                                            // biome-ignore lint/suspicious/noArrayIndexKey: Recap items don't have unique IDs
                                            <Column xs={12} md={6} key={`recap-${recapIndex}`}>
                                                <Container inline>
                                                    <li />
                                                    <Label inline text={recap.code} bold />
                                                    <Label inline text={` ${recap.value || ''}`} />
                                                </Container>
                                            </Column>
                                        );
                                    })}
                                </Row>
                            </Grid>
                        </Container>
                        <Separator orientation={'horizontal'} top={sizes.xs} bottom={sizes.xs} solid />
                    </React.Fragment>
                )}
            </Container>
        );
    };

    const getTabs = () => {
        const tabs = [];

        if (props.risks && props.risks.length > 0) {
            props.risks.forEach((risk) => {
                tabs.push({
                    code: risk.description,
                    description: risk.fullDescription.substring(risk.description.length).trim(),
                });
            });
        }

        return tabs;
    };

    const riskCount = props.risks?.length || 0;
    const multiRisk = riskCount > 1;
    const currentRiskData = props.risks?.[currentRisk];

    const tabsPerColumn = multiRisk === true && riskCount <= 2 ? 6 : riskCount <= 3 ? 4 : 3;

    return (
        <React.Fragment>
            {multiRisk && (
                <Container>
                    <Tabbed items={getTabs()} clickEvent={(value) => setCurrentRisk(value)} lg={tabsPerColumn} />
                </Container>
            )}
            <Grid>
                <Row>
                    <Column nogutter>{currentRiskData && getRisk(currentRiskData)}</Column>
                </Row>
            </Grid>
        </React.Fragment>
    );
};

export default ProfileRisks;
